/**
 * titleFieldsRecovery — recover structured ``title_fields`` from a title page
 * that was drafted as ``**Label:** value`` PROSE in the section body.
 *
 * Faithful client-side port of ``parse_title_fields_from_markdown`` in
 * ``src/csr_sim/title_page_recovery.py`` (the server SSOT). It exists because
 * the two Load paths recover the title differently:
 *
 *   • The whole-file "Load doc" path (``fetchRunDocument`` →
 *     ``hydrateLiveDocFromBase64``) inserts the server-rebuilt .docx, whose
 *     ``_rebuild_docx_for_download`` already recovers the title fields from the
 *     prose. The cover always renders.
 *   • The per-section auto-restore / Load path (``loadRunIntoLiveDoc``) paints
 *     the cover ONLY from a structured ``title_fields`` object returned by
 *     ``/all-sections``. When the drafting model wrote the title page as
 *     ``**Label:** value`` prose and left ``title_fields`` null, that path had
 *     nothing to paint and the cover came up blank on first load — even though
 *     the same run rendered correctly via "Load doc".
 *
 * Recovering the fields here, from the TITLE section's own body, closes that
 * gap so the first-time load matches the download by construction. When the
 * server already supplied structured ``title_fields`` the caller skips this
 * entirely, so it is a pure fallback and never overrides real data.
 *
 * Pure, deterministic, zero IO. Returns ``{}`` when nothing parses so callers
 * can treat an empty result as "no reconstruction" and fall through unchanged.
 */

// ``**Label:** value`` — the colon lives inside the bold span. Non-greedy label
// so it ends at the FIRST ``:**`` (a value may legitimately contain ``**``).
const TITLE_FIELD_LINE = /^\s*\*\*(.+?):\*\*\s*(.*)$/;

// Lines carrying a leaked subtask-manifest sentinel — drop them before parsing
// so the last field's value doesn't absorb one. Mirrors the server's
// ``_MANIFEST_MARKER_LINE`` (case-insensitive "SUBTASK-MANIFEST").
const MANIFEST_MARKER_LINE = /SUBTASK-MANIFEST/i;

/**
 * Recover ``title_fields`` from a title page drafted as ``**Label:** value``
 * prose. Inline ``[LANGUAGE:]`` / ``[MISSING INFO:]`` flags are preserved
 * verbatim in the values (downstream flag styling keys off them). A field whose
 * value is a following markdown table (its own line is ``**Label:**`` with no
 * inline value) keeps the raw table markdown as its value.
 */
export function parseTitleFieldsFromMarkdown(draftFull: string | null | undefined): Record<string, string> {
  if (!draftFull || !draftFull.trim()) return {};

  const fields: Record<string, string> = {};
  let currentLabel: string | null = null;
  let currentParts: string[] = [];

  const flush = (): void => {
    if (currentLabel !== null) {
      const value = currentParts.join("\n").trim();
      // Last write wins — a repeated label keeps its final value.
      if (value) fields[currentLabel] = value;
    }
    currentLabel = null;
    currentParts = [];
  };

  for (const line of draftFull.split("\n")) {
    if (MANIFEST_MARKER_LINE.test(line)) continue;
    const m = TITLE_FIELD_LINE.exec(line);
    if (m) {
      flush();
      currentLabel = m[1].trim();
      const inline = m[2].trim();
      currentParts = inline ? [inline] : [];
    } else if (currentLabel !== null) {
      // Continuation of the current field (e.g. a markdown table body).
      currentParts.push(line);
    }
  }
  flush();

  return fields;
}
